import { Document, Packer, Paragraph, TextRun } from 'docx';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { stripHtml, formatParagraphs, stripMarkdown } from '../utils/textUtils';

export type FileFormat = 'pdf' | 'docx' | 'txt';

export const exportFile = async (
  content: string,
  fileName: string,
  format: FileFormat,
  header?: string,
  footer?: string
): Promise<void> => {
  try {
    const cleanHeader = header ? stripHtml(header) : '';
    const cleanFooter = footer ? stripHtml(footer) : '';
    const paragraphs = formatParagraphs(content);

    switch (format) {
      case 'pdf':
        await exportToPDF(paragraphs, fileName, cleanHeader, cleanFooter);
        break;
      case 'docx':
        await exportToWord(paragraphs, fileName, cleanHeader, cleanFooter);
        break;
      case 'txt':
        exportToTxt(paragraphs, fileName, cleanHeader, cleanFooter);
        break;
      default:
        throw new Error('Formato não suportado');
    }
  } catch (error) {
    console.error('Error exporting file:', error);
    throw new Error('Erro ao exportar arquivo. Por favor, tente novamente.');
  }
};

const exportToPDF = async (
  paragraphs: string[],
  fileName: string,
  header: string,
  footer: string
): Promise<void> => {
  const pdfDoc = await PDFDocument.create();
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  
  // PDF settings
  const fontSize = 11;
  const lineHeight = fontSize * 1.5;
  const margin = 50;
  const pageWidth = 612; // Standard US Letter width in points
  const pageHeight = 792; // Standard US Letter height in points
  const textWidth = pageWidth - (2 * margin);
  
  const createPage = () => {
    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    return {
      page,
      currentY: pageHeight - margin
    };
  };
  
  let { page, currentY } = createPage();

  // Helper function to write a line of text
  const writeLine = (text: string, font: typeof helveticaFont, x: number, y: number) => {
    page.drawText(text, {
      x,
      y,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
    return font.widthOfTextAtSize(text, fontSize);
  };

  // Helper function to write wrapped text
  const writeWrappedText = async (text: string, isBold: boolean = false) => {
    const font = isBold ? helveticaBold : helveticaFont;
    const words = text.split(' ');
    let currentLine = '';
    // let spaceWidth = font.widthOfTextAtSize(' ', fontSize);
    
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = font.widthOfTextAtSize(testLine, fontSize);
      
      if (testWidth > textWidth) {
        // Write current line
        writeLine(currentLine, font, margin, currentY);
        currentLine = word;
        currentY -= lineHeight;
        
        // Check if we need a new page
        if (currentY < margin + lineHeight) {
          const newPageData = createPage();
          page = newPageData.page;
          currentY = newPageData.currentY;
        }
      } else {
        currentLine = testLine;
      }
    }
    
    // Write remaining text
    if (currentLine) {
      writeLine(currentLine, font, margin, currentY);
      currentY -= lineHeight;
    }
    
    // Add extra space after paragraph
    currentY -= lineHeight / 2;
    
    // Check if we need a new page for the next paragraph
    if (currentY < margin + lineHeight * 2) {
      const newPageData = createPage();
      page = newPageData.page;
      currentY = newPageData.currentY;
    }
  };

  // Write header if present
  if (header) {
    await writeWrappedText(header);
    currentY -= lineHeight;
  }

  // Write paragraphs
  for (const paragraph of paragraphs) {
    const parts = paragraph.split(/(\*\*.*?\*\*)/g);
    
    for (const part of parts) {
      if (part.startsWith('**') && part.endsWith('**')) {
        await writeWrappedText(part.slice(2, -2), true);
      } else if (part.trim()) {
        await writeWrappedText(part);
      }
    }
  }

  // Write footer on each page if present
  if (footer) {
    const pages = pdfDoc.getPages();
    for (const currentPage of pages) {
      const footerLines = footer.split('\n');
      let footerY = margin + lineHeight * (footerLines.length - 1);
      
      for (const line of footerLines) {
        currentPage.drawText(line, {
          x: margin,
          y: footerY,
          size: fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
        footerY -= lineHeight;
      }
    }
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  saveAs(blob, `${fileName}.pdf`);
};

// Word and TXT export functions remain the same
const exportToWord = async (
  paragraphs: string[],
  fileName: string,
  header: string,
  footer: string
): Promise<void> => {
  const children = [];

  if (header) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: header, size: 24 })],
        spacing: { after: 400 }
      })
    );
  }

  paragraphs.forEach(paragraph => {
    const parts = paragraph.split(/(\*\*.*?\*\*)/g);
    const runs = parts.map(part => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return new TextRun({
          text: part.slice(2, -2),
          bold: true,
          size: 24
        });
      }
      return new TextRun({
        text: part,
        size: 24
      });
    });

    children.push(
      new Paragraph({
        children: runs,
        spacing: { before: 200, after: 200 }
      })
    );
  });

  if (footer) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: footer, size: 24 })],
        spacing: { before: 400 }
      })
    );
  }

  const doc = new Document({
    sections: [{
      properties: {},
      children
    }]
  });

  const buffer = await Packer.toBlob(doc);
  saveAs(buffer, `${fileName}.docx`);
};

const exportToTxt = (
  paragraphs: string[],
  fileName: string,
  header: string,
  footer: string
): void => {
  const content = [
    header,
    ...paragraphs.map(p => stripMarkdown(p)),
    footer
  ]
    .filter(Boolean)
    .join('\n\n');
  
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  saveAs(blob, `${fileName}.txt`);
};
